/**
 * Step 3: Situación Fiscal del Cliente
 * Versión refactorizada con TanStack Form
 */
import { useStore } from '@tanstack/react-store'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Building2, MapPin } from 'lucide-react'
import { ActividadesSelector } from '@/components/ui/actividades-selector'
import { ImpuestosSelector } from '@/components/ui/impuestos-selector'
import { useCatalogoImpuestos } from '@/hooks/useCatalogoImpuestos'
import { CONDICIONES_IVA, CATEGORIAS_MONOTRIBUTO } from '@/workspaces/contable/types/faceta'
import { cn } from '@/lib/utils'
import { getFieldError, type ClienteForm } from '../useClienteForm'
import type { ActividadEconomica } from '../clienteSchema'
import type { CatalogoImpuesto, ImpuestoLiquidar } from '@/types/impuestos'

interface StepFiscalProps {
  form: ClienteForm
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
]

// Código de cada impuesto de Ganancias que lleva anticipo, con el código de su anticipo.
const ANTICIPO_DE_GANANCIAS: Record<string, string> = {
  ganancias_ph: 'ganancias_anticipo_ph',
  ganancias_juridica: 'ganancias_anticipo_jur',
}

// Suma el anticipo de cada Ganancias que pasó de destildado a tildado.
// El anticipo se busca por código en el catálogo activo y no se duplica si ya estaba.
// Destildar Ganancias o tildar solo el anticipo no cambia nada.
const sumarAnticipoGanancias = (
  previos: ImpuestoLiquidar[],
  siguientes: ImpuestoLiquidar[],
  catalogo: CatalogoImpuesto[],
): ImpuestoLiquidar[] => {
  const idsPrevios = new Set(previos.map((imp) => imp.impuesto_id))
  const resultado = [...siguientes]
  for (const imp of siguientes) {
    if (idsPrevios.has(imp.impuesto_id)) continue
    const codigoAnticipo = ANTICIPO_DE_GANANCIAS[imp.codigo]
    if (!codigoAnticipo) continue
    const anticipo = catalogo.find((c) => c.codigo === codigoAnticipo && c.activo)
    if (!anticipo) continue
    const yaEsta = resultado.some(
      (r) => r.impuesto_id === anticipo.id || r.codigo === anticipo.codigo,
    )
    if (yaEsta) continue
    resultado.push({ impuesto_id: anticipo.id, codigo: anticipo.codigo })
  }
  return resultado
}

export function StepFiscalNew({ form }: StepFiscalProps) {
  const {
    catalogo: catalogoImpuestos,
  } = useCatalogoImpuestos()
  // Valores de otros campos que deciden qué se muestra. Se leen suscriptos al store: pueden cambiar
  // con la ficha del cliente, o desde otro campo, mientras el paso está montado.
  const condicionIva = useStore(form.store, (s) => s.values.condicion_iva)
  const domicilioProvincia = useStore(form.store, (s) => s.values.domicilio_provincia)
  const tipoPersona = useStore(form.store, (s) => s.values.tipo_persona)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-2 border-b">
        <div className="p-2 rounded-lg bg-primary/10">
          <Building2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h3 className="font-medium">Situación Fiscal</h3>
          <p className="text-sm text-muted-foreground">Datos impositivos del cliente</p>
        </div>
      </div>

      {/* Condición ARCA + Categoría Monotributo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <form.Field name="condicion_iva">
          {(field) => (
            <div className="space-y-2">
              <Label className="text-base font-semibold">Condición ante ARCA</Label>
              <Select
                value={field.state.value || ''}
                onValueChange={(value) => {
                  field.handleChange(value || null)
                  // Limpiar categoría si no es monotributo
                  if (value !== 'monotributo' && value !== 'monotributo_social') {
                    form.setFieldValue('categoria_monotributo', null)
                    // Resetear simplificado si deja de ser monotributista
                    if (form.state.values.regimen_iibb === 'simplificado') {
                      form.setFieldValue('regimen_iibb', null)
                    }
                  }
                }}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Seleccionar condición" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CONDICIONES_IVA).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </form.Field>

        <form.Field name="condicion_iva">
          {(condicionField) => {
            const esMonotributo =
              condicionField.state.value === 'monotributo' ||
              condicionField.state.value === 'monotributo_social'

            if (!esMonotributo) return null

            return (
              <form.Field name="categoria_monotributo">
                {(field) => (
                  <div className="space-y-2">
                    <Label>Categoría Monotributo</Label>
                    <Select
                      value={field.state.value || ''}
                      onValueChange={(value) => field.handleChange(value || null)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIAS_MONOTRIBUTO.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            Categoría {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            )
          }}
        </form.Field>
      </div>

      {/* Actividades Económicas */}
      <form.Field name="actividades_economicas">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-2">
              <Label className={cn('text-base font-semibold', hasError && 'text-destructive')}>
                Actividades Económicas *
              </Label>
              <ActividadesSelector
                value={(field.state.value || []) as ActividadEconomica[]}
                onChange={(actividades) => {
                  field.handleChange(actividades)
                  // Actualizar campos de compatibilidad con la actividad principal
                  const principal = actividades[0]
                  form.setFieldValue('actividad', principal?.descripcion || null)
                  form.setFieldValue('codigo_actividad_afip', principal?.codigo || null)
                }}
              />
              <p className={cn('text-sm', hasError ? 'text-destructive' : 'text-muted-foreground')}>
                {hasError
                  ? getFieldError(field.state.meta.errors)
                  : 'La primera actividad será la principal, las demás secundarias.'}
              </p>
            </div>
          )
        }}
      </form.Field>

      {/* Régimen Ingresos Brutos */}
      <form.Field name="regimen_iibb">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0
          return (
            <div className="space-y-3">
              <Label className={cn('text-base font-semibold', hasError && 'text-destructive')}>
                Régimen de Ingresos Brutos *
              </Label>
              <RadioGroup
                value={field.state.value || ''}
                onValueChange={(value) => {
                  const newValue = value as 'local' | 'convenio_multilateral' | 'simplificado' | 'no_gravado' | null
                  field.handleChange(newValue || null)
                  // Auto-completar provincia desde domicilio cuando es local
                  if (value === 'local') {
                    const provinciaFromForm = form.state.values.domicilio_provincia
                    if (provinciaFromForm) {
                      form.setFieldValue('provincia', provinciaFromForm)
                    }
                  }
                }}
                className="grid grid-cols-2 gap-3"
              >
                <Label
                  htmlFor="iibb-local"
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all',
                    field.state.value === 'local'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-primary/50',
                    hasError && 'border-destructive'
                  )}
                >
                  <RadioGroupItem value="local" id="iibb-local" />
                  <div>
                    <span className="font-medium">Local</span>
                    <p className="text-xs text-muted-foreground">Una sola provincia</p>
                  </div>
                </Label>
                <Label
                  htmlFor="iibb-convenio"
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all',
                    field.state.value === 'convenio_multilateral'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-primary/50',
                    hasError && 'border-destructive'
                  )}
                >
                  <RadioGroupItem value="convenio_multilateral" id="iibb-convenio" />
                  <div>
                    <span className="font-medium">Convenio</span>
                    <p className="text-xs text-muted-foreground">Multijurisdiccional</p>
                  </div>
                </Label>
                {(condicionIva === 'monotributo' || condicionIva === 'monotributo_social') && (
                  <Label
                    htmlFor="iibb-simplificado"
                    className={cn(
                      'flex items-center gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all',
                      field.state.value === 'simplificado'
                        ? 'border-primary bg-primary/5'
                        : 'border-muted hover:border-primary/50',
                      hasError && 'border-destructive'
                    )}
                  >
                    <RadioGroupItem value="simplificado" id="iibb-simplificado" />
                    <div>
                      <span className="font-medium">Simplificado</span>
                      <p className="text-xs text-muted-foreground">Incluido en monotributo</p>
                    </div>
                  </Label>
                )}
                <Label
                  htmlFor="iibb-no-gravado"
                  className={cn(
                    'flex items-center gap-3 rounded-lg border-2 p-4 cursor-pointer transition-all',
                    field.state.value === 'no_gravado'
                      ? 'border-primary bg-primary/5'
                      : 'border-muted hover:border-primary/50',
                    hasError && 'border-destructive'
                  )}
                >
                  <RadioGroupItem value="no_gravado" id="iibb-no-gravado" />
                  <div>
                    <span className="font-medium">No Gravado</span>
                    <p className="text-xs text-muted-foreground">No alcanzado por IIBB</p>
                  </div>
                </Label>
              </RadioGroup>
              {hasError && (
                <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>
              )}

              {field.state.value === 'local' && (
                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="text-sm">
                    <span className="text-muted-foreground">Provincia de IIBB: </span>
                    <span className="font-medium">
                      {domicilioProvincia || 'Completar domicilio primero'}
                    </span>
                  </div>
                </div>
              )}

              {field.state.value === 'convenio_multilateral' && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    El cliente liquida por Convenio Multilateral. Las provincias se configuran según corresponda.
                  </p>
                </div>
              )}

              {field.state.value === 'simplificado' && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Ingresos Brutos incluido en la cuota del monotributo. No requiere presentación de DDJJ separada.
                  </p>
                </div>
              )}

              {field.state.value === 'no_gravado' && (
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    El cliente no está alcanzado por el impuesto a los Ingresos Brutos.
                  </p>
                </div>
              )}
            </div>
          )
        }}
      </form.Field>

      {/* Impuestos a Liquidar */}
      <form.Field name="impuestos_liquidar">
        {(field) => (
          <div className="space-y-2">
            <Label className="text-base font-semibold">Impuestos a Liquidar</Label>
            <p className="text-sm text-muted-foreground">
              Selecciona los impuestos que el estudio debe presentar para este cliente
            </p>
            <ImpuestosSelector
              catalogo={catalogoImpuestos}
              value={field.state.value || []}
              onChange={(impuestos) =>
                field.handleChange(
                  sumarAnticipoGanancias(field.state.value || [], impuestos, catalogoImpuestos),
                )
              }
            />
          </div>
        )}
      </form.Field>

      {/* Cierre de Ejercicio (solo sociedades) + Cliente Desde */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {tipoPersona === 'juridica' && (
          <form.Field name="cierre_ejercicio">
            {(field) => {
              const hasError = field.state.meta.errors.length > 0
              return (
                <div className="space-y-2">
                  <Label className={cn(hasError && 'text-destructive')}>Cierre de Ejercicio *</Label>
                  <Select
                    value={field.state.value || ''}
                    onValueChange={(value) => field.handleChange(value || null)}
                  >
                    <SelectTrigger className={cn('h-11', hasError && 'border-destructive')}>
                      <SelectValue placeholder="Seleccionar mes" />
                    </SelectTrigger>
                    <SelectContent>
                      {MESES.map((mes) => (
                        <SelectItem key={mes} value={mes}>
                          {mes}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasError && <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>}
                </div>
              )
            }}
          </form.Field>
        )}

        <form.Field name="cliente_desde">
          {(field) => {
            const hasError = field.state.meta.errors.length > 0
            const today = new Date().toISOString().split('T')[0]
            return (
              <div className="space-y-2">
                <Label htmlFor="cliente_desde" className={cn(hasError && 'text-destructive')}>
                  Cliente Desde *
                </Label>
                <Input
                  id="cliente_desde"
                  type="date"
                  max={today}
                  value={field.state.value || ''}
                  onChange={(e) => field.handleChange(e.target.value || null)}
                  onBlur={field.handleBlur}
                  className={cn('h-11', hasError && 'border-destructive')}
                />
                {hasError && <p className="text-sm text-destructive">{getFieldError(field.state.meta.errors)}</p>}
              </div>
            )
          }}
        </form.Field>
      </div>

    </div>
  )
}
